import {useEffect, useState} from "react";
import {UseFieldArrayRemove} from "react-hook-form";
import {usePricingCurrencies} from "@/features/currencies";
import {Currency} from "../../../../../calls/useCases/createService/types";

/**
 * The "one row per currency" bookkeeping every offering form's Pricing section needs:
 * which codes are still free to pick, and what a freshly added row starts as.
 *
 * Shared rather than per offering type. The four forms had a copy each and the copies
 * were identical, so a rule change had to be made four times to take effect once.
 *
 * The parameters are structural on purpose. A Single Fee row always carries an amount and
 * a Pay What You Want row does not, so the two spell their row type differently; this
 * hook only ever reads `callPriceCurrency`, and asking for no more than that is what lets
 * both shapes pass.
 */
export function useCurrencyOptions(
  currencyArray: readonly {callPriceCurrency: string}[],
  defaultCurrency: string,
  functionToAppendNewCurrency: (row: Currency) => void,
  funcToRemoveCurrency: UseFieldArrayRemove,
  /**
   * What a freshly added row starts with. A Single Fee row starts at 1, the way it always
   * has; a Pay What You Want row starts empty, because 1 is below the Platform Minimum
   * and the coach must set both amounts themselves.
   */
  newRowAmounts: Omit<Currency, "callPriceCurrency"> = {callPrice: 1}
) {
  const {formCurrencies} = usePricingCurrencies();
  const [currencyOptions, setCurrencyOptions] = useState(formCurrencies);

  function unpickedCurrencies() {
    const alreadyPickedCurrencies = [
      ...currencyArray.map((currency) => currency.callPriceCurrency.toLowerCase()),
      defaultCurrency.toLowerCase(),
    ];

    return formCurrencies.filter(
      (currencyOption) => !alreadyPickedCurrencies.includes(currencyOption.toLowerCase())
    );
  }

  useEffect(() => {
    const alreadyPickedCurrencies = [
      ...currencyArray.map((currency) => currency.callPriceCurrency.toLowerCase()),
      defaultCurrency.toLowerCase(),
    ];
    const newCurrencyOptions = formCurrencies.filter(
      (currencyOption) => !alreadyPickedCurrencies.includes(currencyOption.toLowerCase())
    );

    setCurrencyOptions(newCurrencyOptions);
  }, [currencyArray, defaultCurrency, formCurrencies]);

  function addNewCurrency() {
    const newCurrencyOptions = unpickedCurrencies();

    const copyNewCurrencyOptions = [...newCurrencyOptions];
    newCurrencyOptions.shift();
    setCurrencyOptions(newCurrencyOptions);
    functionToAppendNewCurrency({
      callPriceCurrency: copyNewCurrencyOptions[0],
      ...newRowAmounts,
    });
  }

  function removeCurrency(index: number) {
    const newCurrencyOptions = unpickedCurrencies();
    funcToRemoveCurrency(index);
    setCurrencyOptions(newCurrencyOptions);
  }

  function handleCurrencySelectChange(
    value: string,
    onChangeFunc: (value: string) => void
  ) {
    onChangeFunc(value);
    const alreadyPickedCurrencies = [
      ...currencyArray.map((currency) => currency.callPriceCurrency.toLowerCase()),
      defaultCurrency,
    ];
    const newCurrencyOptions = formCurrencies.filter(
      (currencyOption) =>
        !alreadyPickedCurrencies.includes(currencyOption.toLowerCase()) &&
        currencyOption.toLowerCase() !== value
    );

    setCurrencyOptions(newCurrencyOptions);
  }

  return {
    currencyOptions,
    addNewCurrency,
    removeCurrency,
    handleCurrencySelectChange,
  };
}
